/**
 * Discovery Artefacts Hook
 * Manages Goals, Interviews, and Competitors with CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type DiscoveryType = 'goals' | 'interviews' | 'competitors';

export interface DiscoveryGoal {
  id: string;
  title: string;
  description: string | null;
  successMetrics: string | null;
  timeline: string | null;
  category: 'BUSINESS' | 'USER' | 'PRODUCT' | 'TECHNICAL' | null;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DiscoveryAttachment[];
  createdBy: { id: string; name: string | null; email: string };
}

export interface DiscoveryInterview {
  id: string;
  stakeholderName: string;
  stakeholderTitle: string | null;
  stakeholderOrg: string | null;
  interviewDate: string | null;
  keyInsights: string | null;
  painPoints: string | null;
  influence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  fullNotes: string | null;
  generatedFrom: Record<string, unknown> | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DiscoveryAttachment[];
  createdBy: { id: string; name: string | null; email: string };
}

export interface DiscoveryCompetitor {
  id: string;
  name: string;
  category: 'DIRECT' | 'INDIRECT' | 'SUBSTITUTE' | null;
  threatLevel: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  marketPosition: string | null;
  strengths: string | null;
  weaknesses: string | null;
  marketShare: string | null;
  marketShareSource: string | null;
  sourceLinks: string[] | null;
  generatedFrom: Record<string, unknown> | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  attachments: DiscoveryAttachment[];
  createdBy: { id: string; name: string | null; email: string };
  // Extended fields for richer competitor analysis
  description: string | null;
  productOffering: string | null;
  targetAudience: string | null;
  pricingModel: string | null;
  keyDifferentiators: string | null;
  fundingStatus: string | null;
  foundedYear: string | null;
  headquarters: string | null;
  employeeCount: string | null;
  recentNews: string | null;
  strategicMoves: string | null;
  technologyStack: string | null;
  customerSentiment: string | null;
  opportunities: string | null;
  threats: string | null;
}

export interface DiscoveryAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  uploadedAt: string;
}

export interface DiscoveryVersion {
  id: string;
  version: number;
  snapshot: unknown;
  changelog: string | null;
  source: 'MANUAL' | 'AI_GENERATED' | 'RESTORED' | 'IMPORTED';
  createdAt: string;
  createdBy: { id: string; name: string | null; email: string };
}

export interface DiscoveryArtefact {
  id: string;
  type: 'GOALS' | 'INTERVIEWS' | 'COMPETITORS';
  status: 'DRAFT' | 'REVIEWED' | 'APPROVED';
  items: DiscoveryGoal[] | DiscoveryInterview[] | DiscoveryCompetitor[];
  versionsCount: number;
  createdAt: string;
  updatedAt: string;
}

// API Functions
async function fetchDiscovery(
  projectId: string,
  type: DiscoveryType,
  token: string
): Promise<DiscoveryArtefact> {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch discovery data');
  }

  const data = await response.json();
  return data.data;
}

async function createDiscoveryRow(
  projectId: string,
  type: DiscoveryType,
  rowData: Record<string, unknown>,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/rows`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(rowData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create row');
  }

  return response.json();
}

async function updateDiscoveryRow(
  projectId: string,
  type: DiscoveryType,
  rowId: string,
  rowData: Record<string, unknown>,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/rows/${rowId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(rowData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update row');
  }

  return response.json();
}

async function deleteDiscoveryRow(
  projectId: string,
  type: DiscoveryType,
  rowId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/rows/${rowId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete row');
  }

  return response.json();
}

async function fetchVersions(
  projectId: string,
  type: DiscoveryType,
  token: string
): Promise<DiscoveryVersion[]> {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/versions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch versions');
  }

  const data = await response.json();
  return data.data;
}

async function restoreVersion(
  projectId: string,
  type: DiscoveryType,
  versionId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/versions/${versionId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to restore version');
  }

  return response.json();
}

async function generateDiscoveryContent(
  projectId: string,
  type: DiscoveryType,
  prompt: string,
  attachedFiles: { name: string; type: string; size: number }[],
  model: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      prompt,
      attachedFiles,
      model,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate content');
  }

  return response.json();
}

// Hooks
export function useDiscovery(projectId: string, type: DiscoveryType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['discovery', projectId, type],
    queryFn: () => fetchDiscovery(projectId, type, token!),
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useDiscoveryVersions(projectId: string, type: DiscoveryType) {
  const token = useAuthToken();

  return useQuery({
    queryKey: ['discovery-versions', projectId, type],
    queryFn: () => fetchVersions(projectId, type, token!),
    enabled: !!projectId && !!token,
    staleTime: 30000,
  });
}

export function useCreateDiscoveryRow(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (rowData: Record<string, unknown>) =>
      createDiscoveryRow(projectId, type, rowData, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['discovery-versions', projectId, type] });
    },
  });
}

export function useUpdateDiscoveryRow(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ rowId, data }: { rowId: string; data: Record<string, unknown> }) =>
      updateDiscoveryRow(projectId, type, rowId, data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['discovery-versions', projectId, type] });
    },
  });
}

export function useDeleteDiscoveryRow(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (rowId: string) =>
      deleteDiscoveryRow(projectId, type, rowId, token!),
    onMutate: async (rowId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['discovery', projectId, type] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData<DiscoveryArtefact>(['discovery', projectId, type]);
      
      // Optimistically update - remove item immediately
      if (previousData) {
        queryClient.setQueryData<DiscoveryArtefact>(['discovery', projectId, type], {
          ...previousData,
          items: previousData.items.filter((item: any) => item.id !== rowId),
        });
      }
      
      return { previousData };
    },
    onError: (err, rowId, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['discovery', projectId, type], context.previousData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['discovery-versions', projectId, type] });
    },
  });
}

export function useRestoreVersion(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (versionId: string) =>
      restoreVersion(projectId, type, versionId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['discovery-versions', projectId, type] });
    },
  });
}

export function useGenerateDiscovery(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (params: {
      prompt: string;
      attachedFiles?: { name: string; type: string; size: number }[];
      model?: string;
    }) =>
      generateDiscoveryContent(
        projectId,
        type,
        params.prompt,
        params.attachedFiles || [],
        params.model || 'deepseek-chat',
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
      queryClient.invalidateQueries({ queryKey: ['discovery-versions', projectId, type] });
    },
  });
}

// Attachment functions
async function addAttachment(
  projectId: string,
  type: DiscoveryType,
  rowId: string,
  attachment: { fileName: string; fileType: string; fileSize: number; filePath: string },
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/rows/${rowId}/attachments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(attachment),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add attachment');
  }

  return response.json();
}

async function deleteAttachment(
  projectId: string,
  type: DiscoveryType,
  rowId: string,
  attachmentId: string,
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/rows/${rowId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete attachment');
  }

  return response.json();
}

export function useAddAttachment(projectId: string, type: DiscoveryType, rowId: string) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (attachment: { fileName: string; fileType: string; fileSize: number; filePath: string }) =>
      addAttachment(projectId, type, rowId, attachment, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
    },
  });
}

export function useDeleteAttachment(projectId: string, type: DiscoveryType, rowId: string) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      deleteAttachment(projectId, type, rowId, attachmentId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
    },
  });
}

// Status update
async function updateArtefactStatus(
  projectId: string,
  type: DiscoveryType,
  status: 'DRAFT' | 'REVIEWED' | 'APPROVED',
  token: string
) {
  const response = await fetch(`/api/projects/${projectId}/discovery/${type}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update status');
  }

  return response.json();
}

export function useUpdateArtefactStatus(projectId: string, type: DiscoveryType) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: (status: 'DRAFT' | 'REVIEWED' | 'APPROVED') =>
      updateArtefactStatus(projectId, type, status, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery', projectId, type] });
    },
  });
}

